import CustomAutoComplete, { CustomAutoCompleteProps } from '../../UI/CustomAutoComplete';
import { Worktype } from '../../../types/Worktype.interface';
import { useMemo } from 'react';
import { AutocompleteValue } from '@mui/material/useAutocomplete/useAutocomplete';

export type WorktypeAutocompleteProps<Multiple extends boolean | undefined = false> = {
    worktypeOptions: Worktype[];
    selectedWorktype: null | Worktype | Worktype[];
    onChange: (worktypes: null | Worktype | Worktype[]) => void;
    sortByName?: boolean;
    isMultiSelect?: Multiple;
    label?: string;
} & Omit<CustomAutoCompleteProps<Worktype, Multiple>, 'options' | 'label' | 'value' | 'onChange' | 'labelKeys'>;

function WorktypeAutocomplete<Multiple extends boolean | undefined = false>({
    selectedWorktype,
    onChange,
    worktypeOptions,
    isMultiSelect,
    sortByName = true,
    label = 'Worktype',
}: WorktypeAutocompleteProps<Multiple>) {
    const handleChangeSelectedWorktypes = (worktypes: AutocompleteValue<Worktype, Multiple, false, false>) => {
        if (Array.isArray(worktypes)) {
            const allWorktypesObject = worktypes.find((worktype) => worktype.id === -1);
            if (allWorktypesObject && worktypes.length > 1) {
                if (worktypes.indexOf(allWorktypesObject) === worktypes.length - 1) {
                    onChange([allWorktypesObject]);
                    return;
                } else {
                    onChange(worktypes.filter((worktype) => worktype.id !== allWorktypesObject.id));
                    return;
                }
            }
        }

        onChange(worktypes);
    };

    const sortedOptions = useMemo(() => {
        return sortByName
            ? [...worktypeOptions].sort((a, b) => {
                  if (a.id === -1) return -1;
                  if (b.id === -1) return 1;

                  return a.worktype.localeCompare(b.worktype);
              })
            : worktypeOptions;
    }, [worktypeOptions, sortByName]);

    return (
        <CustomAutoComplete
            isMultiSelect={isMultiSelect}
            options={sortedOptions}
            labelKeys={['worktype']}
            value={selectedWorktype}
            label={label}
            onChange={handleChangeSelectedWorktypes}
            isOptionEqualToValue={(option: Worktype, value: Worktype) => option.id === value.id}
        />
    );
}

export default WorktypeAutocomplete;
